import { useCallback, useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { api, apiError, eur, API } from "@/lib/apiClient";
import { toast } from "sonner";
import { StatusBadge } from "@/components/StatusBadge";
import { ADMIN_STATE_OPTIONS, STATE_META } from "@/lib/constants";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Slider } from "@/components/ui/slider";
import { ReinvioProposte } from "@/components/ReinvioProposte";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Check, X, Wallet, ShieldCheck, FileText, SlidersHorizontal, Loader2, Copy } from "lucide-react";
import { BulkBar, RowActions, RowCheck, useBulkSelect } from "@/components/AdminBulkActions";

const PRODUCT_NAMES = {
  rc_economia_solidale: "RC Economia Solidale",
  mrv_volontariato: "Multirischi Volontariato",
  infortuni_economia_solidale: "Infortuni Economia Solidale",
  cat_nat_impresa: "CAT NAT Assimoco",
  multirischi_beni: "Multirischi Danni ai Beni",
  casa_multirischi: "Tutela Multirischi per l'Abitazione",
  cooperazione_salute: "Cooperazione Salute 4You",
  d_o: "D&O Directors & Officers",
  eticapro_infortuni_formula_service: "Eticapro Infortuni — Formula Service",
};

const QUOTA_ROUTES = {
  rc_economia_solidale: "/admin/quota/nuovo",
  mrv_volontariato: "/admin/quota/mrv",
  infortuni_economia_solidale: "/admin/quota/infortuni",
  cat_nat_impresa: "/admin/quota/catnat",
  multirischi_beni: "/admin/quota/multirischi",
  casa_multirischi: "/admin/quota/casa",
  cooperazione_salute: "/admin/quota/cooperazione-salute",
  d_o: "/admin/quota/do",
};

const LOCKED_STATES = ["pagato", "polizza_emessa", "perfezionato", "non_perfezionato"];

export default function QuotesRegistry() {
  const [items, setItems] = useState([]);
  const [config, setConfig] = useState(null);
  const [detail, setDetail] = useState(null);
  const [newState, setNewState] = useState("");
  const [authQuote, setAuthQuote] = useState(null);
  const [form, setForm] = useState(null);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState("");
  const [propostaNum, setPropostaNum] = useState("");
  const [pfConfig, setPfConfig] = useState(null);
  const [pfForm, setPfForm] = useState(null);
  const [adesione, setAdesione] = useState(null);
  const [showArchived, setShowArchived] = useState(false);
  const sel = useBulkSelect();
  const navigate = useNavigate();

  const load = useCallback(() => api.get(`/admin/quotes${showArchived ? "?include_archived=true" : ""}`).then(({ data }) => { setItems(data); sel.clear(); }), [showArchived]); // eslint-disable-line
  useEffect(() => { setPropostaNum(detail?.numero_proposta || ""); }, [detail]);
  useEffect(() => { load(); api.get("/rc/config").then(({ data }) => setConfig(data)); api.get("/public/protezione-famiglia/config").then(({ data }) => setPfConfig(data)).catch(() => {}); }, [load]);

  const [searchParams, setSearchParams] = useSearchParams();
  const statoF = searchParams.get("stato");
  const shown = statoF ? items.filter((x) => x.stato === statoF) : items;
  const clearStato = () => { searchParams.delete("stato"); setSearchParams(searchParams, { replace: true }); };
  useEffect(() => {
    const openId = searchParams.get("open");
    if (!openId || items.length === 0) return;
    const q = items.find((x) => x.id === openId);
    if (q) { setDetail(q); setNewState(q.stato); }
    searchParams.delete("open");
    setSearchParams(searchParams, { replace: true });
  }, [items, searchParams, setSearchParams]);

  useEffect(() => {
    if (detail?.pf_input) {
      setPfForm({ pacchetto: detail.pf_input.pacchetto, nucleo: detail.pf_input.nucleo,
        disabilita_grave: !!detail.pf_input.disabilita_grave });
    } else { setPfForm(null); }
    if (detail?.id) {
      api.get(`/quotes/${detail.id}/adesione`).then(({ data }) => setAdesione(data)).catch(() => setAdesione(null));
    } else { setAdesione(null); }
  }, [detail]);

  const creaLinkAdesione = () => act(async () => {
    const { data } = await api.post(`/quotes/${detail.id}/adesione/link`, {});
    const url = `${window.location.origin}/adesione/${data.token}`;
    try { await navigator.clipboard.writeText(url); toast.success("Link adesione copiato — invialo al cliente"); }
    catch { toast.success("Link creato"); }
    const { data: st } = await api.get(`/quotes/${detail.id}/adesione`);
    setAdesione(st);
  });

  const savePf = () => act(async () => {
    const { data } = await api.post(`/protezione-famiglia/quotes/${detail.id}/modifica`, pfForm);
    toast.success(`Preventivo aggiornato — premio ${eur(data.premio_finale)}`);
    setDetail(null);
  });

  const inviaModulo = () => act(async () => {
    const { data } = await api.post(`/quotes/${detail.id}/adesione/invia-modulo`, {});
    toast.success(`Modulo di Adesione inviato a ${data.email}`);
    const { data: st } = await api.get(`/quotes/${detail.id}/adesione`);
    setAdesione(st);
  });

  const inviaLinkAdesione = () => act(async () => {
    const { data } = await api.post(`/quotes/${detail.id}/adesione/invia-link`, {});
    toast.success(`Link di adesione inviato a ${data.email}`);
    const { data: st } = await api.get(`/quotes/${detail.id}/adesione`);
    setAdesione(st);
  });

  const act = async (fn) => {
    setBusy("x");
    try { await fn(); await load(); }
    catch (e) { toast.error(apiError(e)); }
    finally { setBusy(""); }
  };

  const reject = (id) => act(async () => { await api.post(`/admin/quotes/${id}/referral/decisione`, { esito: "rifiutato", note: "" }); toast.success("Referral rifiutato"); });
  const approve = (id) => act(async () => { await api.post(`/admin/quotes/${id}/referral/decisione`, { esito: "approvato", note: "" }); toast.success("Referral approvato"); });
  const incasso = (id) => act(async () => { await api.post(`/admin/quotes/${id}/incasso`); toast.success("Bonifico confermato"); });
  const emissione = (id) => act(async () => { await api.post(`/admin/quotes/${id}/emissione-polizza`); toast.success("Polizza emessa"); });
  const saveProposta = () => act(async () => {
    await api.post(`/admin/quotes/${detail.id}/proposta`, { numero_proposta: propostaNum });
    setDetail((d) => (d ? { ...d, numero_proposta: propostaNum } : d));
    toast.success("Numero di Proposta registrato: la causale del bonifico è stata aggiornata");
  });
  const openPdf = (id) => window.open(`${API}/quotes/${id}/pdf?view=cliente`, "_blank");

  const changeState = () => act(async () => {
    await api.post(`/admin/quotes/${detail.id}/stato`, { stato: newState });
    toast.success(`Stato aggiornato: ${STATE_META[newState]?.label || newState}`);
    setDetail(null);
  });

  const openAuthorize = (q) => {
    setAuthQuote(q);
    const rco = (q.optional_coverages || []).find((o) => o.coverage_code === "rco");
    const organi = (q.optional_coverages || []).find((o) => o.coverage_code === "organi_sociali");
    const tutela = (q.optional_coverages || []).find((o) => o.coverage_code === "tutela_legale");
    setForm({
      section_code: q.section_code, parameter_code: q.parameter_code, parameter_value: q.parameter_value,
      massimale: q.massimale, rco_massimale: q.rco_massimale || q.massimale, risk_class: q.risk_class || "1",
      slider_pct: q.slider_pct || 0, underwriting_answers: q.underwriting_answers || {},
      maggiorazioni: q.maggiorazioni || [], note: "",
      rco: !!rco, organi: !!organi, tutela: !!tutela,
      tutela_option: tutela?.option || "tutti_componenti", tutela_soci: tutela?.soci || 0,
      organi_massimale: organi?.massimale || 25000,
    });
    setPreview(null);
  };

  const buildOptional = (f) => {
    const oc = [];
    if (f.rco) oc.push({ coverage_code: "rco", selected: true });
    if (f.organi) oc.push({ coverage_code: "organi_sociali", selected: true, massimale: Number(f.organi_massimale) });
    if (f.tutela) oc.push({ coverage_code: "tutela_legale", selected: true, option: f.tutela_option, massimale: 15000, soci: Number(f.tutela_soci) });
    return oc;
  };

  const authPayload = (f) => ({
    section_code: f.section_code, parameter_code: f.parameter_code, parameter_value: Number(f.parameter_value),
    massimale: Number(f.massimale), rco_massimale: Number(f.rco_massimale), risk_class: f.risk_class,
    slider_pct: f.slider_pct, underwriting_answers: f.underwriting_answers, maggiorazioni: f.maggiorazioni,
    optional_coverages: buildOptional(f),
  });

  const recalc = async () => {
    try {
      const { data } = await api.post("/quotes/calculate", authPayload(form));
      setPreview(data);
    } catch (e) { toast.error(apiError(e)); }
  };

  const authorize = () => act(async () => {
    await api.post(`/admin/quotes/${authQuote.id}/autorizza`, { ...authPayload(form), note: form.note });
    toast.success("Preventivo autorizzato");
    setAuthQuote(null);
  });

  const section = config?.sections?.[form?.section_code];

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-heading text-3xl font-semibold text-forest">Registro preventivi</h1>
        <p className="mt-1 text-muted-foreground">Worklist Agenzia: referral, incassi, emissioni. Le provvigioni partner non sono visibili.</p>
      </div>
      <ReinvioProposte />

      <Card className="overflow-hidden rounded-xl border-border/60">
        <div className="p-3">
          <BulkBar endpoint="/admin/quotes/bulk" selected={sel.selected} onDone={load}
            showArchived={showArchived} onToggleArchived={() => setShowArchived((s) => !s)} entityLabel="preventivi" />
          {statoF && (
            <button onClick={clearStato} data-testid="quotes-stato-filter" className="mt-2 inline-flex items-center gap-1.5 rounded-full bg-forest px-3 py-1 text-xs font-semibold text-white">
              Solo: {STATE_META[statoF]?.label || statoF} · {shown.length} <span aria-hidden>✕</span>
            </button>
          )}
        </div>
        <Table>
          <TableHeader><TableRow className="bg-sage/50">
            <TableHead className="w-10"><Checkbox checked={shown.length > 0 && sel.count >= shown.length} onCheckedChange={() => sel.toggleAll(shown.map((x) => x.id))} data-testid="select-all" /></TableHead>
            <TableHead>Pratica</TableHead><TableHead>Prodotto</TableHead><TableHead>Cliente finale</TableHead><TableHead>Partner</TableHead><TableHead>Binario</TableHead>
            <TableHead className="text-right">Premio</TableHead><TableHead>Stato</TableHead><TableHead>Azioni</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {shown.length === 0 && <TableRow><TableCell colSpan={9} className="py-10 text-center text-muted-foreground">Nessun preventivo.</TableCell></TableRow>}
            {shown.map((q) => (
              <TableRow key={q.id} data-testid={`admin-quote-${q.id}`}>
                <TableCell><RowCheck id={q.id} selected={sel.isSelected(q.id)} onToggle={sel.toggle} /></TableCell>
                <TableCell className="cursor-pointer font-medium text-forest hover:text-terracotta" onClick={() => { setDetail(q); setNewState(q.stato); }}>#{q.id.slice(-6).toUpperCase()}</TableCell>
                <TableCell className="text-sm text-muted-foreground" data-testid={`quote-product-${q.id}`}>{PRODUCT_NAMES[q.product_code] || q.product_code}</TableCell>
                <TableCell className="text-sm font-medium text-forest" data-testid={`quote-cliente-${q.id}`}>{q.cliente_nome || "—"}</TableCell>
                <TableCell className="text-sm" data-testid={`quote-partner-${q.id}`}>{q.partner_nome || q.codice_collab || "—"}</TableCell>
                <TableCell><span className={`text-xs font-medium ${q.binario === "standard" ? "text-emerald-600" : "text-amber-600"}`}>{q.binario === "standard" ? "Standard" : "Referral"}</span></TableCell>
                <TableCell className="text-right font-medium">{eur(q.premio_finale)}</TableCell>
                <TableCell><StatusBadge state={q.stato} /></TableCell>
                <TableCell>
                  <div className="flex flex-wrap gap-1">
                    {["referral_inviato", "bozza", "referral_rifiutato"].includes(q.stato) && q.binario === "referral_tecnico" && (
                      <>
                        {q.product_code === "rc_economia_solidale" ? (
                          <Button size="sm" variant="ghost" onClick={() => openAuthorize(q)} data-testid={`authorize-${q.id}`} className="h-7 gap-1 text-emerald-600"><SlidersHorizontal className="h-3.5 w-3.5" /> Autorizza</Button>
                        ) : (
                          <Button size="sm" variant="ghost" onClick={() => approve(q.id)} data-testid={`approve-${q.id}`} className="h-7 gap-1 text-emerald-600"><Check className="h-3.5 w-3.5" /> Approva</Button>
                        )}
                        <Button size="sm" variant="ghost" onClick={() => reject(q.id)} data-testid={`reject-${q.id}`} className="h-7 gap-1 text-red-600"><X className="h-3.5 w-3.5" /></Button>
                      </>
                    )}
                    {q.stato === "in_attesa_bonifico" && (
                      <Button size="sm" variant="ghost" onClick={() => incasso(q.id)} data-testid={`incasso-${q.id}`} className="h-7 gap-1 text-forest"><Wallet className="h-3.5 w-3.5" /> Incasso</Button>
                    )}
                    {q.stato === "pagato" && (
                      <Button size="sm" variant="ghost" onClick={() => emissione(q.id)} data-testid={`emissione-${q.id}`} className="h-7 gap-1 text-forest"><ShieldCheck className="h-3.5 w-3.5" /> Emetti</Button>
                    )}
                    <Button size="sm" onClick={() => { setDetail(q); setNewState(q.stato); }} data-testid={`manage-quote-${q.id}`} className="h-7 gap-1 bg-forest text-xs hover:bg-forest/90"><SlidersHorizontal className="h-3.5 w-3.5" /> Gestisci</Button>
                    <Button size="sm" variant="ghost" onClick={() => openPdf(q.id)} data-testid={`pdf-quote-${q.id}`} title="Anteprima PDF" className="h-7 gap-1 text-muted-foreground"><FileText className="h-3.5 w-3.5" /></Button>
                    <RowActions endpoint="/admin/quotes/bulk" id={q.id} archived={showArchived} onDone={load} />
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>

      {/* Detail + change state */}
      <Dialog open={!!detail} onOpenChange={() => setDetail(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader><DialogTitle className="font-heading text-forest">Pratica #{detail?.id.slice(-6).toUpperCase()}</DialogTitle></DialogHeader>
          {detail && (
            <div className="space-y-3 py-1 text-sm">
              <div className="flex justify-between"><span className="text-muted-foreground">Cliente finale</span><span className="font-medium text-forest" data-testid="detail-cliente">{detail.cliente_nome || "—"}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Partner</span><span className="font-medium text-forest">{detail.partner_nome || detail.codice_collab || (detail.owner === "agenzia" ? "Agenzia" : "—")}</span></div>
              {detail.pf_input ? (
                <>
                  <div className="flex justify-between"><span className="text-muted-foreground">Pacchetto</span><span className="font-medium text-forest">{detail.pf_input.pacchetto_nome}</span></div>
                  <div className="flex justify-between"><span className="text-muted-foreground">Nucleo</span><span>{detail.pf_input.nucleo_label}</span></div>
                  <div className="flex justify-between"><span className="text-muted-foreground">Disabilità grave</span><span>{detail.pf_input.disabilita_grave ? "Sì" : "No"}</span></div>
                </>
              ) : detail.product_code === "rc_economia_solidale" || !detail.product_code ? (
                <>
                  <div className="flex justify-between"><span className="text-muted-foreground">Sezione</span><span>{detail.section_code}</span></div>
                  <div className="flex justify-between"><span className="text-muted-foreground">Parametro</span><span>{detail.parameter_code} = {detail.parameter_value}</span></div>
                  <div className="flex justify-between"><span className="text-muted-foreground">Massimale RCT / RCO</span><span>{eur(detail.massimale)} / {eur(detail.rco_massimale || detail.massimale)}</span></div>
                  <div className="flex justify-between"><span className="text-muted-foreground">Leva commerciale</span><span>{detail.slider_pct > 0 ? "+" : ""}{detail.slider_pct}%</span></div>
                </>
              ) : null}
              <div className="flex justify-between border-t border-border pt-2 font-medium text-forest"><span>Premio lordo</span><span>{eur(detail.premio_finale)}</span></div>
              {detail.piano_rate && (
                <div className="rounded-lg bg-sage/50 p-2 text-xs">
                  <div className="mb-1 font-medium text-forest">Piano rate ({detail.piano_rate.frazionamento}) · scad. annuale {detail.piano_rate.scadenza_annuale}</div>
                  {detail.piano_rate.rate.map((r, i) => <div key={i} className="flex justify-between"><span>{r.descrizione} — {r.scadenza}</span><span>{eur(r.importo)}</span></div>)}
                </div>
              )}
              <div className="border-t border-border pt-3">
                <Label className="text-xs uppercase tracking-wide text-muted-foreground">Cambia stato (override Agenzia)</Label>
                <div className="mt-2 flex gap-2">
                  <Select value={newState} onValueChange={setNewState}>
                    <SelectTrigger data-testid="change-state-select" className="h-9"><SelectValue /></SelectTrigger>
                    <SelectContent>{ADMIN_STATE_OPTIONS.map((s) => <SelectItem key={s} value={s}>{STATE_META[s]?.label || s}</SelectItem>)}</SelectContent>
                  </Select>
                  <Button size="sm" onClick={changeState} disabled={busy === "x" || newState === detail.stato} data-testid="change-state-button" className="bg-terracotta hover:bg-terracotta-hover">Applica</Button>
                </div>
              </div>
              <div className="border-t border-border pt-3">
                <Label className="text-xs uppercase tracking-wide text-muted-foreground">Numero di Proposta autorizzato (compagnia)</Label>
                <p className="mt-1 text-[11px] text-muted-foreground">Registralo per completare la causale del bonifico che il Partner invia al cliente.</p>
                <div className="mt-2 flex gap-2">
                  <Input data-testid="proposta-input" value={propostaNum} onChange={(e) => setPropostaNum(e.target.value)} placeholder="es. 9682/2026" className="h-9" />
                  <Button size="sm" onClick={saveProposta} disabled={busy === "x" || !propostaNum.trim()} data-testid="save-proposta-button" className="bg-forest hover:bg-forest/90">Salva</Button>
                </div>
                {detail.numero_proposta && <p className="mt-1 text-[11px] text-forest">Attuale: <b>{detail.numero_proposta}</b></p>}
              </div>
              {detail.product_code === "rc_economia_solidale" && (
                <div className="border-t border-border pt-3">
                  <Button size="sm" onClick={() => { openAuthorize(detail); setDetail(null); }} data-testid="detail-edit-authorize" className="w-full gap-1.5 bg-emerald-600 hover:bg-emerald-700">
                    <SlidersHorizontal className="h-4 w-4" /> Modifica, ricalcola e autorizza
                  </Button>
                </div>
              )}
              {!detail.pf_input && detail.product_code !== "rc_economia_solidale" && QUOTA_ROUTES[detail.product_code] && !LOCKED_STATES.includes(detail.stato) && (
                <div className="border-t border-border pt-3">
                  <Button size="sm" onClick={() => navigate(`${QUOTA_ROUTES[detail.product_code]}?edit=${detail.id}`)} data-testid="detail-edit-engine" className="w-full gap-1.5 bg-emerald-600 hover:bg-emerald-700">
                    <SlidersHorizontal className="h-4 w-4" /> Modifica nel preventivatore
                  </Button>
                  <p className="mt-1.5 text-[11px] text-muted-foreground">L'Agenzia può correggere e ricalcolare il preventivo del partner mantenendone l'intestazione.</p>
                </div>
              )}
              {detail.pf_input && pfConfig && pfForm && !["pagato", "polizza_emessa", "perfezionato", "non_perfezionato"].includes(detail.stato) && (
                <div className="border-t border-border pt-3 space-y-3" data-testid="pf-edit-block">
                  <Label className="text-xs uppercase tracking-wide text-muted-foreground">Modifica preventivo (Protezione Famiglia)</Label>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1.5">
                      <Label>Pacchetto</Label>
                      <Select value={pfForm.pacchetto} onValueChange={(v) => setPfForm({ ...pfForm, pacchetto: v })}>
                        <SelectTrigger data-testid="pf-edit-package" className="h-9"><SelectValue /></SelectTrigger>
                        <SelectContent>{pfConfig.packages.map((p) => <SelectItem key={p.code} value={p.code}>{p.nome}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-1.5">
                      <Label>Nucleo</Label>
                      <Select value={pfForm.nucleo} onValueChange={(v) => setPfForm({ ...pfForm, nucleo: v })}>
                        <SelectTrigger data-testid="pf-edit-nucleo" className="h-9"><SelectValue /></SelectTrigger>
                        <SelectContent>{pfConfig.nuclei.map((n) => <SelectItem key={n.code} value={n.code}>{n.label}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                  </div>
                  <label className="flex items-center gap-2 text-sm"><Checkbox checked={pfForm.disabilita_grave} onCheckedChange={(v) => setPfForm({ ...pfForm, disabilita_grave: !!v })} data-testid="pf-edit-disabilita" /> Disabilità grave (raddoppio capitale)</label>
                  <div className="flex justify-between rounded-lg bg-sage/50 p-2 text-sm">
                    <span className="text-muted-foreground">Nuovo premio lordo</span>
                    <span className="font-medium text-forest">{eur(pfConfig.packages.find((p) => p.code === pfForm.pacchetto)?.premi?.[pfForm.nucleo])}</span>
                  </div>
                  <Button size="sm" onClick={savePf} disabled={busy === "x"} data-testid="pf-edit-save" className="w-full gap-1.5 bg-emerald-600 hover:bg-emerald-700">
                    {busy === "x" ? <Loader2 className="h-4 w-4 animate-spin" /> : <><SlidersHorizontal className="h-4 w-4" /> Salva e ricalcola premio</>}
                  </Button>
                </div>
              )}
              {detail.id && (
                <div className="border-t border-border pt-3 space-y-2" data-testid="adesione-block">
                  <div className="flex items-center justify-between">
                    <Label className="text-xs uppercase tracking-wide text-muted-foreground">Adesione a distanza (IVASS)</Label>
                    {adesione?.status === "completata"
                      ? <span className="rounded-full bg-emerald-100 px-2 py-0.5 text-xs font-medium text-emerald-700">Completata</span>
                      : adesione?.exists
                        ? <span className="rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-700">Inviata — in attesa</span>
                        : null}
                  </div>
                  {adesione?.status === "completata" ? (
                    <div className="space-y-1.5 rounded-lg bg-sage/40 p-3 text-xs text-forest/80" data-testid="adesione-completata">
                      <div><b>Cliente:</b> {adesione.cliente?.codice_fiscale} · {adesione.cliente?.email} · {adesione.cliente?.cellulare}</div>
                      <div><b>Data/ora:</b> {adesione.accepted_at ? new Date(adesione.accepted_at).toLocaleString("it-IT") : "—"} · <b>IP:</b> {adesione.ip || "—"}</div>
                      <div className="flex flex-wrap gap-2 pt-1">
                        {(adesione.documenti || []).filter((d) => d.tipo !== "modulo_adesione").map((d) => (
                          <Button key={d.tipo} size="sm" variant="outline" data-testid={`adesione-doc-${d.tipo}`}
                            onClick={() => window.open(`${API}/quotes/${detail.id}/adesione/documento/${d.tipo}`, "_blank")}
                            className="h-7 gap-1 text-xs"><FileText className="h-3 w-3" /> {d.label}</Button>
                        ))}
                      </div>
                      {(adesione.documenti || []).some((d) => d.tipo === "modulo_adesione") && (
                        <div className="mt-1 space-y-1.5 rounded-lg border border-forest/20 bg-white p-2.5" data-testid="adesione-modulo-firmato">
                          <div className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">Modulo di Adesione firmato</div>
                          <p className="text-[11px] text-muted-foreground">Verifica sempre il documento prima di inviarlo al cliente.</p>
                          <div className="flex flex-wrap gap-2">
                            <Button size="sm" variant="outline" data-testid="adesione-verifica-modulo"
                              onClick={() => window.open(`${API}/quotes/${detail.id}/adesione/documento/modulo_adesione`, "_blank")}
                              className="h-7 gap-1 text-xs"><FileText className="h-3 w-3" /> Verifica il PDF</Button>
                            <Button size="sm" data-testid="adesione-invia-modulo" onClick={inviaModulo} disabled={busy === "x"}
                              className="h-7 gap-1 bg-forest text-xs hover:bg-forest/90">
                              {busy === "x" ? <Loader2 className="h-3 w-3 animate-spin" /> : <><Copy className="h-3 w-3" /> Invia il PDF al cliente</>}
                            </Button>
                          </div>
                          {adesione.modulo_inviato_at && (
                            <div className="text-[11px] text-emerald-700" data-testid="adesione-modulo-inviato">
                              Inviato al cliente il {new Date(adesione.modulo_inviato_at).toLocaleString("it-IT")}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  ) : (
                    <>
                      <p className="text-xs text-muted-foreground">Genera un link sicuro da inviare al cliente (WhatsApp/email): raccoglie consensi, documento d'identità fronte/retro e accettazione MUP.</p>
                      <Button size="sm" onClick={creaLinkAdesione} disabled={busy === "x"} data-testid="adesione-crea-link" className="w-full gap-1.5">
                        <Copy className="h-4 w-4" /> {adesione?.exists ? "Copia di nuovo il link adesione" : "Genera e copia link adesione"}
                      </Button>
                      {adesione?.exists && (
                        <>
                          <Button size="sm" onClick={inviaLinkAdesione} disabled={busy === "x"} data-testid="adesione-invia-link" className="w-full gap-1.5 bg-forest hover:bg-forest/90">
                            {busy === "x" ? <Loader2 className="h-4 w-4 animate-spin" /> : <><FileText className="h-4 w-4" /> Invia link al cliente via email</>}
                          </Button>
                          {adesione.link_inviato_at && (
                            <div className="text-[11px] text-emerald-700" data-testid="adesione-link-inviato">
                              Link inviato al cliente il {new Date(adesione.link_inviato_at).toLocaleString("it-IT")}
                            </div>
                          )}
                        </>
                      )}
                    </>
                  )}
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Authorize editor */}
      <Dialog open={!!authQuote} onOpenChange={() => setAuthQuote(null)}>
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg">
          <DialogHeader><DialogTitle className="font-heading text-forest">Autorizza referral — modifica e ricalcola</DialogTitle></DialogHeader>
          {form && config && (
            <div className="space-y-4 py-2">
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label>Sezione</Label>
                  <Select value={form.section_code} onValueChange={(v) => setForm({ ...form, section_code: v, parameter_code: Object.keys(config.sections[v].parameters)[0], massimale: config.sections[v].massimali[0] })}>
                    <SelectTrigger data-testid="auth-section"><SelectValue /></SelectTrigger>
                    <SelectContent>{Object.entries(config.sections).map(([k, s]) => <SelectItem key={k} value={k}>{s.label}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label>Parametro</Label>
                  <Select value={form.parameter_code} onValueChange={(v) => setForm({ ...form, parameter_code: v })}>
                    <SelectTrigger data-testid="auth-parameter"><SelectValue /></SelectTrigger>
                    <SelectContent>{section && Object.entries(section.parameters).map(([k, p]) => <SelectItem key={k} value={k}>{p.label}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5"><Label>Valore</Label><Input data-testid="auth-value" type="number" value={form.parameter_value} onChange={(e) => setForm({ ...form, parameter_value: e.target.value })} /></div>
                <div className="space-y-1.5">
                  <Label>Massimale RCT</Label>
                  <Select value={String(form.massimale)} onValueChange={(v) => setForm({ ...form, massimale: Number(v) })}>
                    <SelectTrigger data-testid="auth-massimale"><SelectValue /></SelectTrigger>
                    <SelectContent>{section?.massimali.map((m) => <SelectItem key={m} value={String(m)}>{eur(m)}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>
              <div className="flex flex-wrap gap-3">
                <label className="flex items-center gap-2 text-sm"><Checkbox checked={form.rco} onCheckedChange={(v) => setForm({ ...form, rco: !!v })} /> RCO</label>
                <label className="flex items-center gap-2 text-sm"><Checkbox checked={form.organi} onCheckedChange={(v) => setForm({ ...form, organi: !!v })} /> Organi Sociali</label>
                <label className="flex items-center gap-2 text-sm"><Checkbox checked={form.tutela} onCheckedChange={(v) => setForm({ ...form, tutela: !!v })} /> Tutela Legale</label>
              </div>
              {form.rco && (
                <div className="space-y-1.5">
                  <Label>Massimale RCO (può differire)</Label>
                  <Select value={String(form.rco_massimale)} onValueChange={(v) => setForm({ ...form, rco_massimale: Number(v) })}>
                    <SelectTrigger data-testid="auth-rco-massimale"><SelectValue /></SelectTrigger>
                    <SelectContent>{section?.massimali.map((m) => <SelectItem key={m} value={String(m)}>{eur(m)}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              )}
              <div>
                <div className="flex justify-between"><Label>Leva commerciale</Label><span className="text-sm font-medium text-forest">{form.slider_pct > 0 ? "+" : ""}{form.slider_pct}%</span></div>
                <Slider className="mt-2" value={[form.slider_pct]} min={-20} max={300} step={5} onValueChange={(v) => setForm({ ...form, slider_pct: v[0] })} />
              </div>
              <div className="space-y-1.5"><Label>Note autorizzazione</Label><Input value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} /></div>

              <Button variant="outline" onClick={recalc} data-testid="auth-recalc" className="w-full gap-1.5"><SlidersHorizontal className="h-4 w-4" /> Ricalcola premio</Button>
              {preview && (
                <div className="rounded-lg bg-sage/50 p-3 text-sm">
                  <div className="flex justify-between"><span className="text-muted-foreground">Esito</span><span className={preview.binario === "standard" ? "text-emerald-600" : "text-amber-600"}>{preview.binario === "standard" ? "Standard" : "Ancora referral"}</span></div>
                  <div className="flex justify-between font-medium text-forest"><span>Premio</span><span>{eur(preview.premio_finale)}</span></div>
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button onClick={authorize} disabled={busy === "x"} data-testid="auth-confirm" className="bg-terracotta hover:bg-terracotta-hover">
              {busy === "x" ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Check className="h-4 w-4" /> Autorizza preventivo</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
